import React, { useEffect, useState, useRef } from 'react'

import { useFormik } from 'formik'
import * as Yup from 'yup'
import { AlertState } from 'global/AlertState'
import callApi from 'services/api'
import Modal from 'utils/Modal'
import { useNavigate } from 'react-router-dom'
import { formatDate_dd_mm_yyyy } from 'utils/DateFormatter'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { loadingState } from 'global/LoaderState'
import { getMasterdata } from '../delinquency/apis'

const AddEmployee = ({
  showModal,
  handleCloseModal,
  fetchEmployees,
  editingData,
}) => {
  const [, setAlertMessage] = useRecoilState(AlertState)
  const loader = useSetRecoilState(loadingState)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [rankList, setRankList] = useState([])
  const [districtList, setDistrictList] = useState([])
  const [postingList, setPostingList] = useState([])
  const [parentUnit, setParentUnit] = useState([])
  const [initialValues, setInitialValues] = useState({
    empCode: '',
    name: '',
    dob: '',
    rank: '',
    beltNumber: '',
    posting: '',
    posting_other: '',
    phoneNumber: '',
    parentUnit:'',
    homeDistrictOne: '',
    homeDistrictOne_other: '',
    homeDistrictTwo: '',
    homeDistrictTwo_other: '',
  })

  const navigate = useNavigate()

  const dobRef = useRef(null)
  const handleRowClick = () => {
    if (dobRef.current) {
      dobRef.current.showPicker?.() // modern browsers support showPicker()
      dobRef.current.focus() // fallback for older browsers
    }
  }
  // ✅ Validation schema
  const validationSchema = Yup.object({
    empCode: Yup.string().required('Unique ID is required'),
    name: Yup.string().required('Name is required'),
    dob: Yup.date().required('Date of Birth is required'),
    rank: Yup.string().required('Rank is required'),
    phoneNumber: Yup.string()
      .required('Phone Number is required')
      .matches(/^[0-9]+$/, 'Phone Number must contain only digits')
      .length(10, 'Phone Number must be exactly 10 digits'),
    beltNumber: Yup.string().required('Belt Number is required'),
    posting: Yup.string().required('Posting is required'),
    posting_other: Yup.string().when("posting", {
      is: "OTHER",
      then: (schema) => schema.required("Please enter posting"),
      otherwise: (schema) => schema.notRequired(),
    }),
    homeDistrictOne: Yup.string().required('Home District is required'),

    homeDistrictOne_other: Yup.string().when("homeDistrictOne", {
      is: "District outside of Haryana",
      then: (schema) => schema.required("Please enter District outside of Haryana"),
      otherwise: (schema) => schema.notRequired(),
    }),
    homeDistrictTwo_other: Yup.string().when("homeDistrictTwo", {
      is: "District outside of Haryana",
      then: (schema) => schema.required("Please enter District outside of Haryana"),
      otherwise: (schema) => schema.notRequired(),
    }),
  })

  // ✅ Fetch dropdown lists
  const getList = async (value) => {
    try {
      const params = new URLSearchParams({
        filter: JSON.stringify({ type: value }),
        sort_order: '1',
        sort_by: 'name',
        page: '1',
        limit: '1000',
      })

      const response = await callApi(
        `filtermaster?${params}`,
        'GET',
        null,
        true,
        true,
        setAlertMessage,
        navigate
      )

      if (response.success) {
        if (value === 'rank') setRankList(response.body.data)
        if (value === 'district') setDistrictList(response.body.data)
        if (value === 'posting') setPostingList(response.body.data)
        if (value === 'parentUnit') setParentUnit(response.body.data)
      }
    } catch (err) {
      setError('Failed to fetch ' + value)
    }
  }

  const withFallback = (obj, fallback = '') =>
    Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v || fallback]))

  const fetchData = async (id) => {
    try {
      setLoading(true)
      const response = await callApi(`employee/${id}`, 'GET', null, true, true)

      const data = response?.body || []
      const rankData = data.rank ? await getMasterdata(data.rank) : null
      // const postingData = data.posting ? await getMasterdata(data.posting) : null

      // #TODO : add posting data as well


      let postingValue = ''
      let postingOtherValue = ''
      if (data.posting === 'OTHER') {
        postingValue = 'OTHER'
        postingOtherValue = data.posting_other || ''
      } else if (data.posting) {
        const postingData = await getMasterdata(data.posting)
        postingValue = postingData?.id || data.posting
      }


      const enrichedEmployee = {
        ...data,
        dob: data.dob?.split("T")[0],
        rank: rankData?.id,
        posting: postingValue,
        posting_other: postingOtherValue,
      }
      // console.log(enrichedEmployee)

      setInitialValues(withFallback(enrichedEmployee))
    } catch (err) {
      console.error('Failed to fetch data', err)
    } finally {
      setLoading(false)
    }
  }

  // ✅ Formik setup
  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true)
      try {
        const url = editingData ? `employee/${editingData.id}` : 'employee/'

        const method = editingData ? 'PUT' : 'POST'
        const response = await callApi(url, method, values, true, true)

        console.log(values)
        if (response.success) {
          if (fetchEmployees) fetchEmployees()
          resetForm()
          handleCloseModal()
        }
      } catch (err) {
        setError('Failed to add employee')
      } finally {
        setLoading(false)
      }
    },
  })

  useEffect(() => {
    try {
      if (editingData) {
        loader(true)
        setLoading(true)
        fetchData(editingData?.id)
      } else {
        setInitialValues({
          empCode: '',
          name: '',
          dob: '',
          rank: '',
          beltNumber: '',
          posting: '',
          parentUnit:'',
          phoneNumber: '',
          homeDistrictOne: '',
          homeDistrictTwo: '',
        })
      }
    } catch (error) {
    } finally {
      setLoading(false)
      loader(false)
    }
  }, [editingData])

  // ✅ Fetch dropdown data on mount
  useEffect(() => {
    if (showModal) {
      getList('rank')
      getList('district')
      getList('posting')
      getList('parentUnit')
    }
  }, [showModal])

  // ✅ Reset form on close
  useEffect(() => {
    if (!showModal) {
      formik.resetForm()
      setError(null)
    }
  }, [showModal])

  return (
    <Modal
      showModal={showModal}
      handleCloseModal={handleCloseModal}
      title={editingData ? 'Update Employee' : 'Add Employee'}
      modalWidth={{ width: '1000px' }}
    >
      {error && <div className="alert alert-danger">{error}</div>}
      <form onSubmit={formik.handleSubmit}>
        {/* {JSON.stringify(formik.values)} */}
        {/* Employee Code */}
        <div className="form-group row mb-3">
          <label className="col-sm-4 col-form-label">
            Unique ID <span style={{ color: 'red' }}>*</span>
          </label>
          <div className="col-sm-8">
            <input
              type="text"
              className="form-control"
              name="empCode"
              placeholder="Unique ID"
              value={formik.values.empCode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.empCode && formik.errors.empCode && (
              <div className="text-danger">{formik.errors.empCode}</div>
            )}
          </div>
        </div>

        {/* Name */}
        <div className="form-group row mb-3">
          <label className="col-sm-4 col-form-label">
            Name <span style={{ color: 'red' }}>*</span>
          </label>
          <div className="col-sm-8">
            <input
              type="text"
              className="form-control"
              name="name"
              placeholder="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.name && formik.errors.name && (
              <div className="text-danger">{formik.errors.name}</div>
            )}
          </div>
        </div>

        {/* Date of Birth */}
        <div
          className="form-group row mb-3"
          onClick={handleRowClick}
          style={{ cursor: 'pointer' }}
        >
          <label className="col-sm-4 col-form-label">Date of Birth</label>
          <div className="col-sm-8">
            <input
              type="date"
              ref={dobRef}
              className="form-control"
              name="dob"
              value={formik.values.dob}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.dob && formik.errors.dob && (
              <div className="text-danger">{formik.errors.dob}</div>
            )}
          </div>
        </div>

        {/* Phone Number */}
        <div className="form-group row mb-3">
          <label className="col-sm-4 col-form-label">
            Phone Number <span style={{ color: 'red' }}>*</span>
          </label>
          <div className="col-sm-8">
            <input
              type="text"
              className="form-control"
              name="phoneNumber"
              placeholder="Phone Number"
              value={formik.values.phoneNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.phoneNumber && formik.errors.phoneNumber && (
              <div className="text-danger">{formik.errors.phoneNumber}</div>
            )}
          </div>
        </div>

        {/* Rank */}
        <div className="form-group row mb-3">
          <label className="col-sm-4 col-form-label">
            Rank <span style={{ color: 'red' }}>*</span>
          </label>
          <div className="col-sm-8">
            <select
              className="form-control"
              name="rank"
              value={formik.values.rank}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <option value="">Select Rank</option>
              {rankList?.map((opt) => (
                <option key={opt.id || opt.value} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </select>
            {formik.touched.rank && formik.errors.rank && (
              <div className="text-danger">{formik.errors.rank}</div>
            )}
          </div>
        </div>

        {/* Belt Number */}
        <div className="form-group row mb-3">
          <label className="col-sm-4 col-form-label">
            Belt Number <span style={{ color: 'red' }}>*</span>
          </label>
          <div className="col-sm-8">
            <input
              type="text"
              className="form-control"
              name="beltNumber"
              placeholder="Belt Number"
              value={formik.values.beltNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.beltNumber && formik.errors.beltNumber && (
              <div className="text-danger">{formik.errors.beltNumber}</div>
            )}
          </div>
        </div>

        {/* Posting */}
        <div className="form-group row mb-3">
          <label className="col-sm-4 col-form-label">
            Posting <span style={{ color: 'red' }}>*</span>
          </label>

          <div className="col-sm-8">
            <select
              className="form-control"
              name="posting"
              value={formik.values.posting}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <option value="">Select Posting</option>

              {postingList?.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}

              {/* Fallback option */}
              <option value="OTHER">Other</option>
            </select>

            {formik.touched.posting && formik.errors.posting && (
              <div className="text-danger">{formik.errors.posting}</div>
            )}

            {/* Show input only if Other is selected */}
            {formik.values.posting === "OTHER" && (
              <input
                type="text"
                className="form-control mt-2"
                name="posting_other"
                placeholder="Please enter posting"
                value={formik.values.posting_other}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            )}

            {formik.touched.posting_other && formik.errors.posting_other && (
              <div className="text-danger">{formik.errors.posting_other}</div>
            )}
          </div>
        </div>

         <div className="form-group row mb-3">
          <label className="col-sm-4 col-form-label">
            Parent Unit ( if posting is in a deputation unit ) 
          </label>
          <div className="col-sm-8">
            <select
              className="form-control"
              name="parentUnit"
              value={formik.values.parentUnit}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <option value="">Select Parent Unit</option>
              {parentUnit?.map((opt) => (
                <option key={opt.id || opt.value} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </select>
            {/* {formik.touched.rank && formik.errors.rank && (
              <div className="text-danger">{formik.errors.rank}</div>
            )} */}
          </div>
        </div>

        {/* homeDistrictOne */}

        <div className="form-group row mb-3">
          <label className="col-sm-4 col-form-label">
            Home District 1 <span style={{ color: 'red' }}>*</span>
          </label>
          <div className="col-sm-8">
            <select
              className="form-control"
              name="homeDistrictOne"
              value={formik.values.homeDistrictOne}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <option value="">Select District</option>
              {districtList?.map((opt) => (
                <option key={opt.id || opt.value} value={opt.id}>
                  {opt.name}
                </option>
              ))}
              <option value="District outside of Haryana">District outside of Haryana</option>
            </select>
            {formik.touched.homeDistrictOne &&
              formik.errors.homeDistrictOne && (
                <div className="text-danger">
                  {formik.errors.homeDistrictOne}
                </div>
              )}

            {formik.values.homeDistrictOne === "District outside of Haryana" && (
              <input
                type="text"
                className="form-control mt-2"
                name="homeDistrictOne_other"
                placeholder="Please enter District outside of Haryana"
                value={formik.values.homeDistrictOne_other}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            )}

            {formik.touched.homeDistrictOne_other && formik.errors.homeDistrictOne_other && (
              <div className="text-danger">{formik.errors.homeDistrictOne_other}</div>
            )}
          </div>
        </div>

        {/* homeDistrictTwo */}
        <div className="form-group row mb-3">
          <label className="col-sm-4 col-form-label">Home District 2</label>
          <div className="col-sm-8">
            <select
              className="form-control"
              name="homeDistrictTwo"
              value={formik.values.homeDistrictTwo}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <option value="">Select District</option>
              {districtList?.map((opt) => (
                <option key={opt.id || opt.value} value={opt.id}>
                  {opt.name}
                </option>
              ))}
              <option value="District outside of Haryana">District outside of Haryana</option>

            </select>
            {formik.touched.homeDistrictTwo &&
              formik.errors.homeDistrictTwo && (
                <div className="text-danger">
                  {formik.errors.homeDistrictTwo}
                </div>
              )}
            {formik.values.homeDistrictTwo === "District outside of Haryana" && (
              <input
                type="text"
                className="form-control mt-2"
                name="homeDistrictTwo_other"
                placeholder="Please enter District outside of Haryana"
                value={formik.values.homeDistrictTwo_other}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            )}

            {formik.touched.homeDistrictTwo_other && formik.errors.homeDistrictTwo_other && (
              <div className="text-danger">{formik.errors.homeDistrictTwo_other}</div>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div className="d-flex justify-content-center mt-4">
          <button
            type="button"
            className="btn btn-secondary me-2"
            onClick={handleCloseModal}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {editingData
              ? loading
                ? 'Updating...'
                : 'Update'
              : loading
                ? 'Submitting...'
                : 'Submit'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default AddEmployee
